import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getCcuaClient } from '~/apis/ccuaApi'
import { useUserState } from '~/atoms/UserState'
import { useReportCcuaGatewayQueryError } from '~/queries/ccuaGatewaySentry'
import {
  AgentCustomStatus,
  AgentCustomStatusRequest,
} from '~/types/AgentCustomStatus'

import {
  type AgentStatus,
  collectAllGatewayItems,
  mapGatewayAgentStatus,
} from '@mangovoice/cx-call-center-user-agents-client'
import { ApiError } from '@mangovoice/types'

export const useGetCustomStatuses = (pbxId?: number) => {
  const { pbx } = useUserState()

  const query = useQuery<AgentCustomStatus[], ApiError>({
    queryKey: ['customStatuses', pbxId],
    queryFn: async () => {
      if (!pbxId) return []
      if (!pbx?.uuid) {
        throw new Error('Missing PBX UUID for CCUA gateway')
      }
      const collected = await collectAllGatewayItems<AgentStatus>((query) =>
        getCcuaClient().listAgentStatuses(pbx.uuid, query),
      )
      if (!collected.ok) {
        throw new Error('Failed to fetch custom statuses')
      }
      return collected.items.map(mapGatewayAgentStatus)
    },
    enabled: !!pbxId,
  })

  useReportCcuaGatewayQueryError({
    enabled: true,
    pbxId: pbx?.id ?? pbxId,
    source: 'custom-statuses',
    queryKey: ['customStatuses', pbxId],
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
  })

  return query
}

export const useCreateCustomStatus = (
  onSuccess?: (data: AgentCustomStatus) => void,
) => {
  const { pbx } = useUserState()

  return useMutation({
    mutationFn: async (data: AgentCustomStatusRequest) => {
      if (!pbx?.uuid) {
        throw new Error('Missing PBX UUID for CCUA gateway')
      }
      const response = await getCcuaClient().createAgentStatus(pbx.uuid, {
        name: data.name,
        description: data.description,
        availability: data.availability,
      })
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to create custom status')
      }
      if (!response.data) {
        throw new Error('No data received')
      }
      return mapGatewayAgentStatus(response.data)
    },
    onSuccess: (data) => {
      onSuccess?.(data)
    },
  })
}

export const useUpdateCustomStatus = (
  onSuccess?: (updatedStatus: AgentCustomStatus) => void,
) => {
  const queryClient = useQueryClient()
  const { pbx } = useUserState()

  return useMutation({
    mutationFn: async ({
      statusId,
      data,
    }: {
      statusId: string
      data: AgentCustomStatusRequest
    }) => {
      if (!pbx?.uuid) {
        throw new Error('Missing PBX UUID for CCUA gateway')
      }
      const response = await getCcuaClient().patchAgentStatus(
        pbx.uuid,
        statusId,
        {
          name: data.name,
          description: data.description,
          availability: data.availability,
        },
      )
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to update custom status')
      }
      if (!response.data) {
        throw new Error('No data received')
      }
      return mapGatewayAgentStatus(response.data)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customStatuses'] })
      onSuccess?.(data)
    },
  })
}

export const useDeleteCustomStatus = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  const { pbx } = useUserState()

  return useMutation({
    mutationFn: async (statusId: string) => {
      if (!pbx?.uuid) {
        throw new Error('Missing PBX UUID for CCUA gateway')
      }
      const response = await getCcuaClient().deleteAgentStatus(
        pbx.uuid,
        statusId,
      )
      if (!response.ok) {
        throw new Error(response.problem || 'Failed to delete custom status')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customStatuses'] })
      onSuccess?.()
    },
  })
}
